import { useStore } from 'zustand';
import * as Dialog from '@radix-ui/react-dialog';
import type { LucideIcon } from '@ui/icons';
import { ArrowLeftRight, Crown, FileText, Paperclip } from '@ui/icons';
import { t } from '@shared/i18n';
import { planUsageStore } from '../model/manager';
import type { FreeLimitType, LimitModalRequest } from '../model/manager';

interface ModalConfig {
  readonly icon: LucideIcon;
  readonly title: string;
  readonly body: string;
  readonly description: string;
  readonly showUpgrade: boolean;
}

// Re-export the type so it can be used by the manager without a circular dep
export type { LimitModalRequest };

function getConfig(type: FreeLimitType, request: LimitModalRequest): ModalConfig {
  const m = t.planUsage;
  const count = request.usageCount;

  switch (type) {
    case 'currencyConversion':
    case 'groupCurrencyConversion':
    case 'scanWithMultiCurrency':
      return {
        icon: ArrowLeftRight,
        title: m.currency.title,
        body: m.currency.body,
        description: m.currency.description,
        showUpgrade: false,
      };
    case 'exportMonthlyLimit':
    case 'exportFilterLimit':
      return {
        icon: FileText,
        title: m.export.title,
        body:
          count === 1
            ? m.export.bodySingular.replace('{count}', String(count))
            : m.export.bodyPlural.replace('{count}', String(count ?? 0)),
        description: m.export.description,
        showUpgrade: false,
      };
    case 'attachmentWeeklyLimit':
    case 'attachmentSizeLimit':
    case 'olderAttachmentLimit':
      return {
        icon: Paperclip,
        title: m.attachment.title,
        body: m.attachment.body.replace('{count}', String(count ?? 0)),
        description: m.attachment.description,
        showUpgrade: false,
      };
    default:
      return {
        icon: Crown,
        title: m.proFeature.title,
        body: m.proFeature.body,
        description: m.proFeature.description,
        showUpgrade: true,
      };
  }
}

/**
 * Global limit-reached / Pro-upsell modal. Mounted once at the app root (AppShell).
 * Driven by the `planUsageStore` vanilla store — any call to `planUsageManager.check()`
 * that finds a limit reached will push a request here, opening this dialog.
 *
 * Always kept in the React tree (open={false} when idle) so Radix can correctly manage
 * the dialog stack when a second dialog (e.g. ExportModal) is already open.
 */
export function PlanUsageLimitModal() {
  const modalRequest = useStore(planUsageStore, (s) => s.modalRequest);
  const closeModal = useStore(planUsageStore, (s) => s.closeModal);

  const isOpen = modalRequest !== null;
  const config = modalRequest ? getConfig(modalRequest.type, modalRequest) : null;

  const handleNotNow = () => {
    const cb = modalRequest?.onNotNow;
    closeModal();
    cb?.();
  };

  const handleUpgrade = () => {
    const cb = modalRequest?.onUpgrade;
    closeModal();
    cb?.();
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleNotNow();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[70] bg-black/40" />
        <Dialog.Content
          aria-describedby="plan-limit-desc"
          className="fixed left-1/2 top-1/2 z-[70] w-[90vw] max-w-[400px] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-3xl bg-surface-raised shadow-overlay focus:outline-none"
        >
          {config && modalRequest ? (
            <>
              {/* Content area */}
              <div className="flex flex-col items-center gap-5 px-6 pb-6 pt-8 text-center">
                {/* Icon badge + title */}
                <div className="flex flex-col items-center gap-2">
                  <span className="flex size-14 items-center justify-center rounded-full bg-primary/10">
                    <config.icon className="size-7 text-primary" />
                  </span>
                  <Dialog.Title className="text-xl font-bold text-primary">
                    {config.title}
                  </Dialog.Title>
                </div>

                {/* Body */}
                <p className="text-[22px] font-semibold leading-tight text-fg-strong">
                  {config.body}
                </p>

                {/* Description + optional reset countdown */}
                <div id="plan-limit-desc" className="flex flex-col gap-2">
                  <p className="text-sm leading-relaxed text-muted">{config.description}</p>
                  {modalRequest.resetDays != null && modalRequest.resetDays > 0 && (
                    <p className="text-sm font-bold text-fg-strong">
                      {t.planUsage.resetsIn.replace('{days}', String(modalRequest.resetDays))}
                    </p>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-col gap-3 px-6 pb-8">
                <button
                  type="button"
                  onClick={handleNotNow}
                  className="flex h-14 w-full cursor-pointer items-center justify-center rounded-2xl border border-border bg-surface text-base font-semibold text-fg hover:bg-surface/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t.planUsage.notNow}
                </button>
                {config.showUpgrade && (
                  <button
                    type="button"
                    onClick={handleUpgrade}
                    className="flex h-14 w-full cursor-pointer items-center justify-center rounded-2xl bg-primary text-base font-semibold text-primary-fg hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {t.planUsage.upgradeToPro}
                  </button>
                )}
              </div>
            </>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
